import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavigationService } from '@/services/navigationService';

/**
 * Reusable hook for components to check if they should open an entity from navigation state
 * 
 * @param entityType - The type of entity this component handles (e.g., 'task', 'warranty')
 * @returns Object with shouldOpen flag and entityId
 * 
 * @example
 * const { shouldOpen, entityId } = useOpenEntity('task');
 * useEffect(() => {
 *   if (shouldOpen && entityId) {
 *     // Open the entity modal
 *     const task = tasks.find(t => t.id === entityId);
 *     if (task) {
 *       setEditingTask(task);
 *       setIsEditModalOpen(true);
 *     }
 *     NavigationService.clearOpenState();
 *   }
 * }, [shouldOpen, entityId, tasks]);
 */
// State key mapping (matches NavigationService)
const ENTITY_STATE_KEYS: Record<string, string> = {
  task: 'openTaskId',
  checklist: 'openChecklistId',
  specification: 'openSpecificationId',
  warranty: 'openWarrantyId',
  selection: 'openSelectionId',
  change_order: 'openChangeOrderId',
  material_order: 'openMaterialOrderId',
  purchase_order: 'openPurchaseOrderId',
  bill: 'openBillId',
  invoice: 'openInvoiceId',
  bid: 'openBidId',
  bid_package: 'openBidPackageId',
  proposal: 'openProposalId',
};

/**
 * Reusable hook for components to check if they should open an entity from navigation state
 * 
 * @param entityType - The type of entity this component handles (e.g., 'task', 'warranty')
 * @returns Object with shouldOpen flag and entityId
 * 
 * @example
 * const { shouldOpen, entityId } = useOpenEntity('task');
 * useEffect(() => {
 *   if (shouldOpen && entityId) {
 *     // Open the entity modal
 *     const task = tasks.find(t => t.id === entityId);
 *     if (task) {
 *       setEditingTask(task);
 *       setIsEditModalOpen(true);
 *     }
 *     NavigationService.clearOpenState();
 *   }
 * }, [shouldOpen, entityId, tasks]);
 */
export function useOpenEntity(entityType: string): {
  shouldOpen: boolean;
  entityId: string | null;
} {
  const location = useLocation();
  const [shouldOpen, setShouldOpen] = useState(false);
  const [entityId, setEntityId] = useState<string | null>(null);

  useEffect(() => {
    const stateKey = ENTITY_STATE_KEYS[entityType] || 'openEntityId';
    const id = (location.state as any)?.[stateKey];

    if (id) {
      setEntityId(id);
      setShouldOpen(true);
    } else {
      setEntityId(null);
      setShouldOpen(false);
    }
  }, [location.state, entityType]);

  return { shouldOpen, entityId };
}


/**
 * A page that opens ONE record because a link asked for it (a "Linked Docs" entry of another document - see NavigationService.linkFor): reads the open-state,
 * clears it (so a refresh does not reopen it), fetches the record by id - it may not be on the page of the list that is showing - and hands it to `open`.
 * A record that no longer exists, or that the caller cannot read, is said so instead of leaving a dead link that does nothing.
 * `ready` holds the lookup back until the page has what `open` needs.
 */
export function useOpenEntityRecord<T>(entityType: string, fetchById: (id: string) => Promise<T | undefined>, open: (record: T) => void, ready = true): void {
  const { shouldOpen, entityId } = useOpenEntity(entityType);
  const fetchRef = useRef(fetchById);
  const openRef = useRef(open);
  fetchRef.current = fetchById;
  openRef.current = open;

  useEffect(() => {
    if (!shouldOpen || !entityId || !ready) return;
    NavigationService.clearOpenState();
    fetchRef.current(entityId)
      .then((record) => {
        if (record) openRef.current(record);
        else window.alert('This document no longer exists, or you do not have access to it.');
      })
      .catch((error) => {
        console.error(`Error opening the ${entityType}:`, error);
        window.alert('This document no longer exists, or you do not have access to it.');
      });
  }, [shouldOpen, entityId, ready, entityType]);
}
